/*
|--------------------------------------------------------------------------
| Product sticky add to bag (mobile)
|--------------------------------------------------------------------------
| Shows once the main Add to bag button has scrolled off the top of the
| screen. Mirrors the main form and forwards taps to it:
|  - no size chosen  -> opens the size drawer
|  - sold out        -> opens the back in stock form
|  - otherwise       -> clicks the main Add to bag button
|
*/
if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      connectedCallback () {
        this.main = document.querySelector('[data-product-add-to-cart]')
        this.mainText = document.querySelector('[data-product-add-to-cart-text]')
        this.anchor = document.querySelector('.prd-Details_Buttons')
        if (!this.main || !this.anchor) return

        this.bis = document.querySelector('[data-product-back-in-stock-button]')
        this.sizeTrigger = document.querySelector('[data-drawers-trigger="product-size-selection"]')
        this.variantTitle = document.querySelector('[data-variant-title]')
        this.priceSource = document.querySelector('.prd-Details_Price')

        this.button = this.querySelector('[data-sticky-button]')
        this.priceEl = this.querySelector('[data-sticky-price]')
        this.sizeEl = this.querySelector('[data-sticky-size]')

        this.mode = 'add'
        this.past = false

        this.sync = this.sync.bind(this)
        this.onClick = this.onClick.bind(this)
        this.update = this.update.bind(this)

        this.button.addEventListener('click', this.onClick)
        // update() is a single getBoundingClientRect read and only writes on change
        window.addEventListener('scroll', this.update, { passive: true })
        window.addEventListener('resize', this.update)

        this.observer = new MutationObserver(this.sync)
        ;[this.main, this.bis, this.priceSource, this.variantTitle]
          .filter(Boolean)
          .forEach(node => this.observer.observe(node, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          }))

        // Also catch layout jumps that don't fire scroll (drawers, scroll locks)
        this.intersection = new IntersectionObserver(this.update)
        this.intersection.observe(this.anchor)

        this.sync()
        this.update()
      }

      disconnectedCallback () {
        this.observer?.disconnect()
        this.intersection?.disconnect()
        window.removeEventListener('scroll', this.update)
        window.removeEventListener('resize', this.update)
        document.body.classList.remove('has-StickyAtc')
      }

      isShown (el) {
        return !!el && el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null
      }

      text (el) {
        return (el?.textContent || '').replace(/\s+/g, ' ').trim()
      }

      copyPrice () {
        if (!this.priceSource) return
        const clone = this.priceSource.cloneNode(true)
        clone.querySelectorAll('noscript, link, script, style').forEach(node => node.remove())
        clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'))
        const html = clone.innerHTML.trim()
        if (html && html !== this.priceEl.innerHTML) this.priceEl.innerHTML = html
      }

      sync () {
        const mainLabel = this.text(this.mainText)
        const needsSize = !!this.sizeTrigger && mainLabel === (this.dataset.chooseSize || '').trim()

        if (needsSize) {
          this.mode = 'size'
          this.button.textContent = mainLabel
          this.button.disabled = false
        } else if (this.isShown(this.bis)) {
          this.mode = 'bis'
          this.button.textContent = this.text(this.bis)
          this.button.disabled = false
        } else if (!this.main.disabled) {
          this.mode = 'add'
          this.button.textContent = mainLabel
          this.button.disabled = false
        } else {
          this.mode = 'none'
          this.button.textContent = mainLabel
          this.button.disabled = true
        }

        this.sizeEl.textContent = this.sizeTrigger && !needsSize ? this.text(this.variantTitle) : ''
        this.copyPrice()
      }

      onClick () {
        if (this.mode === 'size') this.sizeTrigger.click()
        else if (this.mode === 'bis') this.bis.click()
        else if (this.mode === 'add') this.main.click()
      }

      update () {
        // Visible once the whole button block is above the top of the screen
        const past = this.anchor.getBoundingClientRect().bottom < 0
        if (past === this.past && this.hasAttribute('data-ready')) return

        this.past = past
        this.setAttribute('data-ready', '')
        this.classList.toggle('is-visible', past)
        this.setAttribute('aria-hidden', past ? 'false' : 'true')
        this.button.tabIndex = past ? 0 : -1
        document.body.classList.toggle('has-StickyAtc', past)
      }
    },
  )
}
